// src/pages/Admin.jsx
import React, { useEffect, useRef, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { getStorage, ref as sRef, deleteObject } from "firebase/storage";

// Import de l'image d'arrière-plan
import adminBg from "../assets/3108.jpg";

// ---------- PDF.js Canvas Viewer ----------
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// ---------- R2 / Worker ----------
const R2_HOST = "https://videos.educapsules.com";
const r2UrlFromKey = (key) => `${R2_HOST}/v/${encodeURIComponent(key)}`;

export default function Admin() {
  const [tab, setTab] = useState("receipts"); // "receipts" | "referrals" | "students" | "phones" | "codes" | "payments"

  // ---------------- Reçus en attente ----------------
  const [pending, setPending] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // ---------------- Codes de parrainage ----------------
  const [refCodes, setRefCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newDiscount, setNewDiscount] = useState(50);

  // Overlay viewer (image/pdf/vidéo)
  const [preview, setPreview] = useState(null); // { url, mime }
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const [pdfScale, setPdfScale] = useState(1);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);

  // ---------------- Payements profs ----------------
  const [paySection, setPaySection] = useState("2bac"); // "2bac" | "3acfr"
  const [payRows, setPayRows] = useState([]); // [{ profId, displayName, email, subject, referralCount, paidCount, unpaidCount, bonusesLabel }]
  const [payBusy, setPayBusy] = useState(false);
  const [payErr, setPayErr] = useState("");
  const [paidEdits, setPaidEdits] = useState({}); // key: `${profId}__${paySection}` -> number
  const [payConfirm, setPayConfirm] = useState(null); // { key, row, count }
  const [plusFlash, setPlusFlash] = useState(null); // key for transient +1 animation

  const fullscreenVideo = () => {
    try {
      const v = videoRef.current;
      if (!v) return;
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
        return;
      }
      if (v.requestFullscreen) return v.requestFullscreen();
      if (v.webkitRequestFullscreen) return v.webkitRequestFullscreen();
      if (v.webkitEnterFullscreen) return v.webkitEnterFullscreen();
      if (v.msRequestFullscreen) return v.msRequestFullscreen();
      if (v.mozRequestFullScreen) return v.mozRequestFullScreen();
    } catch {}
  };

  // Charger les codes de parrainage
  useEffect(() => {
    // Charger les données pour Payement
    if (tab === "payments") {
      let unsubUsers = null;
      let unsubSubs = null;
      let unsubPayments = null;
      (async () => {
        try {
          setPayErr("");
          // 1) Tous les profs (infos affichage)
          const profMap = new Map();
          unsubUsers = onSnapshot(
            query(collection(db, "users")),
            (snap) => {
              const studentsLevel = new Map();
              const profInfo = new Map();
              snap.forEach((d) => {
                const u = { id: d.id, ...d.data() };
                if ((u.role || "") === "eleve") studentsLevel.set(d.id, (u.level || "2bac").toLowerCase());
                if ((u.role || "") === "prof" || (u.role || "") === "admin") {
                  profInfo.set(d.id, {
                    profId: d.id,
                    displayName: u.displayName || "",
                    email: u.email || "",
                    subject: u.subject || u.matiere || "",
                  });
                }
              });

              // 2) Subscriptions approuvées (inscriptions)
              unsubSubs = onSnapshot(
                query(collection(db, "subscriptions"), where("status", "==", "approved")),
                async (s2) => {
                  try {
                    const approved = [];
                    s2.forEach((d) => approved.push({ id: d.id, ...d.data() }));

                    // 3) Grouper par profId via referrerProfId et filtrer par section
                    const countsByProf = new Map();
                    approved.forEach((x) => {
                      const profId = x.referrerProfId || null;
                      if (!profId) return; // bonus uniquement si parrainage
                      const level = (studentsLevel.get(x.uid) || "2bac").toLowerCase();
                      const isSelected = (paySection === "2bac" && level === "2bac") || (paySection === "3acfr" && level === "3acfr");
                      if (!isSelected) return;
                      countsByProf.set(profId, (countsByProf.get(profId) || 0) + 1);
                    });

                    // 4) Paiements existants
                    const paymentsRows = [];
                    // Note: getDocs est async et peut échouer, d'où le try/catch global
                    const paymentDocs = await getDocs(query(collection(db, "profPayments"), where("section", "==", paySection)));
                    const paidByKey = new Map();
                    paymentDocs.forEach((d) => {
                      const data = d.data() || {};
                      const key = `${data.profId}__${data.section}`;
                      paidByKey.set(key, Number(data.paidCount || 0));
                    });

                    // 5) Construire la table (TOUS les profs)
                    if (profInfo.size === 0) {
                      // Debug: si aucun prof trouvé, on l'indique via l'erreur pour visibilité
                      setPayErr(`Aucun professeur trouvé (sur ${snap.size} utilisateurs). Vérifiez les rôles.`);
                    }

                    Array.from(profInfo.entries()).forEach(([profId, info]) => {
                      const cnt = countsByProf.get(profId) || 0;
                      const key = `${profId}__${paySection}`;
                      const paidCount = paidByKey.get(key) || 0;
                      paymentsRows.push({
                        profId,
                        displayName: info.displayName,
                        email: info.email,
                        subject: info.subject,
                        referralCount: cnt,
                        paidCount,
                        unpaidCount: Math.max(0, cnt - paidCount),
                        bonusesLabel: cnt > 0 ? Array.from({ length: cnt }, () => "+ 50").join(" ") : "",
                      });
                    });

                    // Trier par matière/nom
                    paymentsRows.sort((a, b) => (a.subject || "").localeCompare(b.subject || "") || (a.displayName || "").localeCompare(b.displayName || ""));
                    setPayRows(paymentsRows);
                  } catch (e) {
                    console.error("Erreur calcul paiements:", e);
                    setPayErr("Erreur calcul: " + (e.message || String(e)));
                  }
                },
                (err) => setPayErr("Erreur subscriptions: " + (err.message || String(err)))
              );

            },
            (err) => setPayErr("Erreur users: " + (err.message || String(err)))
          );
        } catch (e) {
          setPayErr(e.message || "Erreur de chargement des payements");
        }
      })();

      return () => {
        unsubUsers && unsubUsers();
        unsubSubs && unsubSubs();
        unsubPayments && unsubPayments();
      };
    }
  }, [tab, paySection]);
  
  // Charger les codes quand l'onglet "codes" est actif
  useEffect(() => {
    if (tab === "codes") {
      const unsub = onSnapshot(collection(db, "refCodes"), (snap) => {
        const codes = [];
        snap.forEach((doc) => {
          codes.push({ id: doc.id, ...doc.data() });
        });
        setRefCodes(codes);
      });
      return () => unsub();
    }
  }, [tab]);

  // Créer un nouveau code de parrainage
  async function createReferralCode() {
    if (!newCode.trim()) {
      alert("Veuillez entrer un code");
      return;
    }

    try {
      const code = newCode.trim().toUpperCase().replace(/-/g, '');
      
      // Vérifier si le code existe déjà
      const existing = await getDoc(doc(db, "refCodes", code));
      if (existing.exists()) {
        alert("Ce code existe déjà");
        return;
      }

      await setDoc(doc(db, "refCodes", code), {
        active: true,
        discount: Number(newDiscount) || 50,
        createdAt: serverTimestamp(),
        usedCount: 0,
        createdBy: "admin"
      });

      alert(`Code ${code} créé avec succès`);
      setNewCode("");
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  }

  // Activer/Désactiver un code
  async function toggleCode(codeId, currentStatus) {
    try {
      await updateDoc(doc(db, "refCodes", codeId), {
        active: !currentStatus,
        updatedAt: serverTimestamp()
      });
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  }

  // Supprimer un code
  async function deleteCode(codeId) {
    if (!window.confirm("Supprimer définitivement ce code ?")) return;
    try {
      await setDoc(doc(db, "refCodes", codeId), {
        active: false,
        deleted: true,
        deletedAt: serverTimestamp()
      }, { merge: true });
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  }

  useEffect(() => {
    setError("");
    let unsub;

    const listen = (withOrder = true) => {
      try {
        const base = collection(db, "receipts");
        const qy = withOrder
          ? query(base, where("status", "==", "pending"), orderBy("createdAt", "desc"))
          : query(base, where("status", "==", "pending"));

        unsub = onSnapshot(
          qy,
          (snap) => {
            const arr = [];
            snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
            
            // Ajouter les URLs de prévisualisation pour les reçus R2
            setPending(arr.map(r => {
              if (r.r2Key) {
                return { ...r, previewUrl: r2UrlFromKey(r.r2Key), mime: r.mime || "" };
              }
              return r;
            }));
            
            if (!withOrder) {
              setError(
                "Index Firestore manquant pour le tri par date. Crée un index: receipts(status ASC, createdAt DESC)."
              );
            }
          },
          (err) => {
            if (withOrder && err?.code === "failed-precondition") {
              listen(false);
              return;
            }
            console.error(err);
            setError("Impossible de charger les reçus : " + (err.code || err.message));
          }
        );
      } catch (e) {
        console.error(e);
        setError("Erreur : " + e.message);
      }
    };

    listen(true);
    return () => unsub && unsub();
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onClick = (e) => {
      const a = e.target.closest("a");
      if (!a || !el.contains(a)) return;
      const href = a.getAttribute("href") || "";
      const looksLikeReceipt = href.includes("/o/receipts%2f");
      if (looksLikeReceipt) {
        e.preventDefault();
        e.stopPropagation();
        const lower = href.toLowerCase();
        let mime = "";
        if (lower.endsWith(".pdf")) mime = "application/pdf";
        else if (/\.(jpg|jpeg|png|webp)$/.test(lower)) mime = "image/*";
        else if (lower.endsWith(".mp4")) mime = "video/mp4";
        setPreview({ url: href, mime });
      }
    };
    el.addEventListener("click", onClick, true);
    return () => el.removeEventListener("click", onClick, true);
  }, []);

  // Reset page/zoom uniquement quand le document PDF change
  useEffect(() => {
    if (!preview?.url) return;
    const kind = guessKind(preview?.mime, preview?.url);
    if (kind === "pdf") {
      setPdfPage(1);
      setPdfScale(1);
    }
  }, [preview?.url]);

  function guessKind(mime = "", url = "") {
    if (mime) {
      if (mime.startsWith("video/")) return "video";
      if (mime === "application/pdf") return "pdf";
      if (mime.startsWith("image/")) return "image";
    }
    const u = url.toLowerCase();
    if (u.endsWith(".mp4")) return "video";
    if (u.endsWith(".pdf")) return "pdf";
    if (/\.(jpg|jpeg|png|webp)$/.test(u)) return "image";
    return "other";
  }

  // ------ Valider / Rejeter ------
  async function handleApprove(r) {
    setBusy(true);
    setError("");
    try {
      // 1) reçu -> approved
      await updateDoc(doc(db, "receipts", r.id), {
        status: "approved",
        updatedAt: serverTimestamp(),
      });

      // 2) users/{uid} -> access approved
      const uref = doc(db, "users", r.uid);
      const usnap = await getDoc(uref);
      if (usnap.exists()) {
        await updateDoc(uref, { access: "approved", updatedAt: serverTimestamp() });
      } else {
        await setDoc(uref, {
          uid: r.uid,
          role: "eleve",
          access: "approved",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // 3) subscriptions/{uid} -> approved
      await setDoc(
        doc(db, "subscriptions", r.uid),
        {
          uid: r.uid,
          status: "approved",
          approvedAt: serverTimestamp(),
          referrerProfId: r.referrerProfId || null,
          referralCode: r.referralCode || null,
          expectedAmount: r.expectedAmount || (r.referralCode ? 550 : 600),
        },
        { merge: true }
      );

      alert("Reçu validé ✅");
    } catch (e) {
      console.error(e);
      setError("Échec de la validation : " + (e.code || e.message));
    } finally {
      setBusy(false);
    }
  }

  // NOUVELLE FONCTION : Valider un élève directement (sans reçu)
  async function approveStudentDirectly(student) {
    if (!window.confirm(`Valider l'élève ${student.displayName || student.email} sans reçu ?`)) return;
    
    setBusy(true);
    setError("");
    try {
      const uid = student.id;

      // 1) Mettre à jour l'utilisateur -> access approved
      await updateDoc(doc(db, "users", uid), { 
        access: "approved", 
        updatedAt: serverTimestamp() 
      });

      // 2) Créer/mettre à jour l'abonnement -> approved
      await setDoc(
        doc(db, "subscriptions", uid),
        {
          uid: uid,
          status: "approved",
          approvedAt: serverTimestamp(),
          referrerProfId: student.referrerProfId || null,
          referralCode: student.referralCode || null,
          expectedAmount: student.referralCode ? 550 : 600,
          directApproval: true, // Marquer comme approuvé directement
        },
        { merge: true }
      );

      alert(`Élève ${student.displayName || student.email} validé directement ✅`);
    } catch (e) {
      console.error(e);
      setError("Échec de la validation directe : " + (e.code || e.message));
    } finally {
      setBusy(false);
    }
  }

  async function handleReject(r) {
    setBusy(true);
    setError("");
    try {
      await updateDoc(doc(db, "receipts", r.id), {
        status: "rejected",
        updatedAt: serverTimestamp(),
      });
      alert("Reçu rejeté ❌");
    } catch (e) {
      console.error(e);
      setError("Échec du rejet : " + (e.code || e.message));
    } finally {
      setBusy(false);
    }
  }

  // ---------------- Onglet Parrainages ----------------
  const [rows, setRows] = useState([]); // {profId, displayName, email, subject, codesWithCounts:[{code,count}], withMe, others}
  const [totals, setTotals] = useState({ approved: 0 });

  useEffect(() => {
    if (tab !== "referrals") return;

    let unsubRefcodes, unsubSubs;
    const load = async () => {
      // 1) lire tous les codes et les grouper par profId
      unsubRefcodes = onSnapshot(collection(db, "refCodes"), async (snap) => {
        const byProf = new Map();
        snap.forEach((d) => {
          const c = { id: d.id, ...d.data() };
          const k = c.profId || "_unknown";
          if (!byProf.has(k)) byProf.set(k, []);
          byProf.get(k).push(c);
        });

        // 2) lire toutes les subscriptions approved pour compter par code
        unsubSubs = onSnapshot(
          query(collection(db, "subscriptions"), where("status", "==", "approved")),
          async (s2) => {
            const approved = [];
            s2.forEach((d) => approved.push({ id: d.id, ...d.data() }));
            const totalApproved = approved.length;

            // comptage par code
            const byCode = new Map();
            approved.forEach((x) => {
              const c = (x.referralCode || "").toUpperCase();
              if (c) byCode.set(c, (byCode.get(c) || 0) + 1);
            });

            // 3) infos users : on commence par charger tous les "profs", puis
            //    on complète en fallback par getDoc(profId) pour les comptes admin qui ont généré des codes.
            const profInfo = new Map();
            const qUsers = await getDocs(collection(db, "users"));
            qUsers.forEach((d) => {
              const u = d.data();
              profInfo.set(d.id, {
                displayName: u.displayName || "-",
                email: u.email || "-",
                subject: u.subject || u.matiere || "-",
              });
            });

            // 4) construire les lignes
            const out = [];
            for (const [profId, codes] of byProf.entries()) {
              // fallback : si aucune info trouvée on tente un getDoc direct
              if (!profInfo.has(profId)) {
                const snapOne = await getDoc(doc(db, "users", profId));
                if (snapOne.exists()) {
                  const u = snapOne.data();
                  profInfo.set(profId, {
                    displayName: u.displayName || "-",
                    email: u.email || "-",
                    subject: u.subject || u.matiere || "-",
                  });
                } else {
                  profInfo.set(profId, { displayName: "-", email: "-", subject: "-" });
                }
              }

              const withMe = approved.filter((x) => x.referrerProfId === profId).length;
              const others = Math.max(0, totalApproved - withMe);
              const info = profInfo.get(profId);

              const codesWithCounts = codes.map((c) => ({
                code: c.id,
                count: byCode.get((c.id || "").toUpperCase()) || 0,
              }));

              out.push({ profId, codesWithCounts, withMe, others, ...info });
            }

            out.sort((a, b) => b.withMe - a.withMe);
            setRows(out);
            setTotals({ approved: totalApproved });
          }
        );
      });
    };

    load();
    return () => {
      unsubRefcodes && unsubRefcodes();
      unsubSubs && unsubSubs();
    };
  }, [tab]);

  // ---------------- Onglet Élèves ----------------
  const [students, setStudents] = useState([]); // users role=eleve
  const [subsMap, setSubsMap] = useState(new Map()); // uid -> subscription doc
  const [studentSort, setStudentSort] = useState("approved"); // approved | pending | lastAccess | createdAt | none
  const storage = getStorage();

  // ---------------- Onglet Téléphones ----------------
  const [phones, setPhones] = useState([]);
  const [phoneQuery, setPhoneQuery] = useState("");

  useEffect(() => {
    if (tab !== "students") return;
    const unsubUsers = onSnapshot(
      query(collection(db, "users"), where("role", "==", "eleve")),
      (snap) => {
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        setStudents(arr);
      }
    );
    const unsubSubs = onSnapshot(collection(db, "subscriptions"), (snap) => {
      const m = new Map();
      snap.forEach((d) => m.set(d.id, { id: d.id, ...d.data() }));
      setSubsMap(m);
    });
    return () => {
      unsubUsers && unsubUsers();
      unsubSubs && unsubSubs();
    };
  }, [tab]);

  useEffect(() => {
    if (tab !== "phones") return;
    (async () => {
      try {
        const res = await fetch("https://videos.educapsules.com/phones", {
          method: "GET",
          headers: { "Accept": "application/json" },
        });
        if (!res.ok) throw new Error("Erreur de récupération Cloudflare");
        const j = await res.json();
        const items = (j.items || []).map((p) => ({
          ...p,
          createdAtISO: p.createdAt || null,
        }));
        items.sort((a,b)=>{
          const ta = Date.parse(a.createdAtISO || "0");
          const tb = Date.parse(b.createdAtISO || "0");
          return tb - ta;
        });
        setPhones(items);
      } catch (e) {
        console.error(e);
        setPhones([]);
      }
    })();
  }, [tab]);

  async function revokeStudent(uid) {
    try {
      await setDoc(
        doc(db, "subscriptions", uid),
        { status: "revoked", revokedAt: serverTimestamp() },
        { merge: true }
      );
      alert("Inscription révoquée. L'élève n'est plus compté.");
    } catch (e) {
      console.error(e);
      alert("Échec de la révocation : " + (e.code || e.message));
    }
  }

  async function hardDeleteStudent(uid) {
    if (!window.confirm("Supprimer définitivement cet élève et ses inscriptions ?")) return;
    try {
      setBusy(true);

      // 1) supprimer abonnement + reçus + user
      const batch = writeBatch(db);
      batch.delete(doc(db, "subscriptions", uid));

      const rs = await getDocs(query(collection(db, "receipts"), where("uid", "==", uid)));
      rs.forEach((d) => batch.delete(doc(db, "receipts", d.id)));

      batch.delete(doc(db, "users", uid));
      await batch.commit();

      // 2) supprimer les fichiers Storage des reçus (si règles Storage autorisent l'admin)
      try {
        for (const d of rs.docs) {
          const p = d.data()?.receiptPath;
          if (p) await deleteObject(sRef(storage, p));
        }
      } catch (e) {
        console.warn("Suppression fichiers Storage reçus: ", e);
      }

      alert("Élève supprimé. Il ne sera plus compté dans les parrainages.");
    } catch (e) {
      console.error(e);
      alert("Échec de la suppression : " + (e.code || e.message));
    } finally {
      setBusy(false);
    }
  }

  // ---------------- UI ----------------
  function PdfCanvasViewerUrl({ url, scale = 1, pageNumber = 1, onLoaded }) {
    const containerRef = React.useRef(null);
    React.useEffect(() => {
      let cancel = false;
      (async () => {
        if (!url || !containerRef.current) return;
        containerRef.current.innerHTML = "";
        const loadingTask = pdfjsLib.getDocument({ url });
        const pdf = await loadingTask.promise;
        if (cancel) return;
        try { onLoaded && onLoaded(pdf.numPages); } catch {}
        const pn = Math.max(1, Math.min(pageNumber || 1, pdf.numPages));
        const page = await pdf.getPage(pn);
        if (cancel) return;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { alpha: false });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.display = "block";
        canvas.style.margin = "12px auto";
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        canvas.oncontextmenu = (e) => e.preventDefault();
        containerRef.current.appendChild(canvas);
        await page.render({ canvasContext: ctx, viewport }).promise;
      })();
      return () => { cancel = true; };
    }, [url, scale, pageNumber, onLoaded]);
    return (
      <div
        ref={containerRef}
        style={{ maxHeight: "70vh", overflow: "auto", userSelect: "none" }}
        onContextMenu={(e) => e.preventDefault()}
      />
    );
  }

  const Viewer = () =>
    !preview ? null : (
      <div
        className="sv-backdrop"
        onClick={() => setPreview(null)}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="sv-inner" onClick={(e) => e.stopPropagation()}>
          {(() => {
            const kind = guessKind(preview.mime, preview.url);
            if (kind === "video") {
              return (
                <div style={{ position:"relative", width:"100%", height:"100%" }}>
                  <video
                    src={preview.url}
                    controls
                    controlsList="nodownload noplaybackrate"
                    ref={videoRef}
                    style={{ width: "100%", height: "100%", background: "#000" }}
                  />
                  <div style={{ position:"absolute", top:8, right:8 }}>
                    <button className="btn btn-sm btn-outline-light" onClick={fullscreenVideo}>Plein écran</button>
                  </div>
                </div>
              );
            }
            if (kind === "image") {
              return (
                <img
                  src={preview.url}
                  alt="reçu"
                  style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
                  draggable={false}
                />
              );
            }
            if (kind === "pdf") {
              return (
                <div className="d-flex flex-column align-items-center w-100" style={{ height: "100%" }}>
                  <PdfCanvasViewerUrl
                    url={preview.url}
                    scale={pdfScale}
                    pageNumber={pdfPage}
                    onLoaded={(n) => setPdfPages(n || 0)}
                  />
                  <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                    <div className="btn-group" role="group" aria-label="Zoom">
                      <button type="button" className="btn btn-sm btn-outline-light" onClick={() => setPdfScale(s => Math.min(2.5, Math.round((s + 0.1) * 10) / 10))} disabled={pdfScale >= 2.5}>Zoom +</button>
                      <button type="button" className="btn btn-sm btn-outline-light" onClick={() => setPdfScale(s => Math.max(0.6, Math.round((s - 0.1) * 10) / 10))} disabled={pdfScale <= 0.6}>Dézoom</button>
                    </div>
                    <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
                      <button className="btn btn-outline-light" type="button" onClick={() => setPdfPage(p => Math.max(1, p - 1))} disabled={pdfPages <= 1 || pdfPage <= 1}>←</button>
                      <input type="number" className="form-control" min={1} max={Math.max(1, pdfPages || 1)} value={pdfPage} onChange={(e) => { const v = Number(e.target.value); if (!Number.isNaN(v)) setPdfPage(Math.max(1, Math.min(v, pdfPages || 1))); }} aria-label="Numéro de page" />
                      <span className="input-group-text">/ {Math.max(1, pdfPages || 1)}</span>
                      <button className="btn btn-outline-light" type="button" onClick={() => setPdfPage(p => Math.min((pdfPages || 1), p + 1))} disabled={pdfPages <= 1 || pdfPage >= (pdfPages || 1)}>→</button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <iframe
                title="Reçu"
                src={preview.url}
                style={{ width: "100%", height: "100%", border: 0, background: "#000" }}
                referrerPolicy="no-referrer"
              />
            );
          })()}
          <button className="btn btn-sm btn-light sv-close" onClick={() => setPreview(null)}>
            Fermer
          </button>
        </div>
      </div>
    );

  return (
    <>
      {/* Arrière-plan Admin */}
      <div
        className="admin-bg"
        aria-hidden="true"
        style={{ "--admin-bg": `url(${adminBg})` }}
      />
      
      <div ref={rootRef} className="container py-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-3">
            <h1 className="h5 mb-0 text-white">Admin</h1>
            {/* Liens vers l'espace élève retirés à la demande */}
          </div>
          <ul className="nav nav-pills">
            <li className="nav-item">
              <button className={"nav-link " + (tab === "receipts" ? "active" : "")} onClick={() => setTab("receipts")}>
                Reçus en attente
              </button>
            </li>
            <li className="nav-item">
              <button className={"nav-link " + (tab === "referrals" ? "active" : "")} onClick={() => setTab("referrals")}>
                Parrainages
              </button>
            </li>
            <li className="nav-item">
              <button className={"nav-link " + (tab === "students" ? "active" : "")} onClick={() => setTab("students")}>
                Élèves
              </button>
            </li>
            <li className="nav-item">
              <button className={"nav-link " + (tab === "phones" ? "active" : "")} onClick={() => setTab("phones")}>
                Téléphones
              </button>
            </li>
            <li className="nav-item">
              <button className={"nav-link " + (tab === "codes" ? "active" : "")} onClick={() => setTab("codes")}>
                Codes
              </button>
            </li>
            <li className="nav-item">
              <button className={"nav-link " + (tab === "payments" ? "active" : "")} onClick={() => setTab("payments")}>
                Payement
              </button>
            </li>
          </ul>
        </div>

        {/* ------- ONGLET REÇUS ------- */}
        {tab === "receipts" && (
          <>
            {error && <div className="alert alert-warning">{error}</div>}
            {pending.length === 0 ? (
              <p className="text-white">Aucun reçu en attente.</p>
            ) : (
              <div className="row g-3">
                {pending.map((r) => (
                  <div key={r.id} className="col-md-4">
                    <div className="card shadow-sm h-100 transparent-card">
                      <div className="card-body">
                        <p className="mb-1"><strong>Élève UID:</strong> {r.uid}</p>
                        <p className="mb-1"><strong>Montant attendu:</strong> {r.expectedAmount || (r.referralCode ? 550 : 600)} DH</p>
                        <p className="mb-3"><strong>Code parrainage:</strong> {r.referralCode || <span className="text-muted">(aucun)</span>}</p>

                        <button
                          type="button"
                          className="btn btn-primary mb-2"
                          onClick={(e) => { 
                            e.preventDefault(); 
                            e.stopPropagation(); 
                            // Utiliser previewUrl pour R2 ou receiptUrl pour Firebase Storage
                            const previewUrl = r.previewUrl || r.receiptUrl;
                            setPreview({ url: previewUrl, mime: r.mime || r.mimeType || "" }); 
                          }}
                        >
                          Ouvrir le reçu
                        </button>

                        <div className="d-flex gap-2">
                          <button className="btn btn-success" onClick={() => handleApprove(r)} disabled={busy}>Valider</button>
                          <button className="btn btn-danger" onClick={() => handleReject(r)} disabled={busy}>Rejeter</button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ------- ONGLET PARRAINAGES ------- */}
        {tab === "referrals" && (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card shadow-sm transparent-card">
                  <div className="card-body text-center">
                    <div className="text-muted">Total approuvé</div>
                    <div className="display-6">{totals.approved}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle table-light transparent-card">
                <thead>
                  <tr>
                    <th>Prof UID</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Matière</th>
                    <th>Codes générés (inscriptions)</th>
                    <th>Inscrits avec ce prof</th>
                    <th>Autres / Sans ce prof</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.profId}>
                      <td><code>{r.profId}</code></td>
                      <td>{r.displayName}</td>
                      <td>{r.email}</td>
                      <td>{r.subject}</td>
                      <td style={{ maxWidth: 420 }}>
                        <ul className="mb-0 list-unstyled">
                          {r.codesWithCounts.map(({ code, count }) => (
                            <li key={code}>
                              <code>{code}</code>{" "}
                              <span className="text-muted">({count})</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>{r.withMe}</td>
                      <td>{r.others}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={7} className="text-muted">Aucun code enregistré.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ------- ONGLET ÉLÈVES ------- */}
        {tab === "students" && (
          <>
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap" style={{maxWidth: 520}}>
              <div className="input-group">
                <span className="input-group-text">Trier par</span>
                <select className="form-select" value={studentSort} onChange={(e)=>setStudentSort(e.target.value)}>
                  <option value="approved">Approved d'abord</option>
                  <option value="pending">Pending d'abord</option>
                  <option value="lastAccess">Dernier accès</option>
                  <option value="createdAt">Création (récent)</option>
                  <option value="none">Sans tri</option>
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm align-middle table-light transparent-card">
                <thead>
                  <tr>
                    <th>UID</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Accès</th>
                    <th>Code</th>
                    <th>Prof parrain</th>
                    <th>Statut inscription</th>
                    <th style={{ width: 320 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const arr = [...students];
                    const getSec = (t) => {
                      try { return t?.seconds || (t?.toDate ? Math.floor(t.toDate().getTime()/1000) : 0); } catch { return 0; }
                    };
                    const isPending = (u) => {
                      const sub = subsMap.get(u.id);
                      return u.access === "pending" || !sub || sub.status === "pending";
                    };
                    arr.sort((a,b)=>{
                      switch (studentSort) {
                        case "approved": {
                          const sa = a.access === "approved" ? 1 : 0;
                          const sb = b.access === "approved" ? 1 : 0;
                          if (sb !== sa) return sb - sa;
                          return (getSec(b.createdAt) - getSec(a.createdAt));
                        }
                        case "pending": {
                          const pa = isPending(a) ? 1 : 0;
                          const pb = isPending(b) ? 1 : 0;
                          if (pb !== pa) return pb - pa;
                          return (getSec(b.createdAt) - getSec(a.createdAt));
                        }
                        case "lastAccess": {
                          return (getSec(b.updatedAt) - getSec(a.updatedAt));
                        }
                        case "createdAt": {
                          return (getSec(b.createdAt) - getSec(a.createdAt));
                        }
                        default:
                          return 0;
                      }
                    });
                    return arr.map((u)=>{
                    const sub = subsMap.get(u.id);
                    const isPending = u.access === "pending" || !sub || sub.status === "pending";
                    
                    return (
                      <tr key={u.id}>
                        <td className="text-truncate" style={{ maxWidth: 220 }}><code>{u.id}</code></td>
                        <td>{u.displayName || "-"}</td>
                        <td>{u.email || "-"}</td>
                        <td>
                          <span className={`badge ${
                            u.access === "approved" ? "bg-success" : 
                            u.access === "pending" ? "bg-warning" : "bg-secondary"
                          }`}>
                            {u.access || "non défini"}
                          </span>
                        </td>
                        <td>{u.referralCode || "-"}</td>
                        <td>{u.referrerProfId || "-"}</td>
                        <td>
                          <span className={`badge ${
                            sub?.status === "approved" ? "bg-success" : 
                            sub?.status === "pending" ? "bg-warning" : "bg-secondary"
                          }`}>
                            {sub?.status || "non défini"}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2 flex-wrap">
                            {/* Bouton pour valider directement les élèves en pending */}
                            {isPending && (
                              <button 
                                className="btn btn-success btn-sm" 
                                disabled={busy} 
                                onClick={() => approveStudentDirectly(u)}
                                title="Valider cet élève sans reçu"
                              >
                                Valider Directement
                              </button>
                            )}
                            <button className="btn btn-outline-warning btn-sm" disabled={busy} onClick={() => revokeStudent(u.id)}>
                              Révoquer
                            </button>
                            <button className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => hardDeleteStudent(u.id)}>
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    });
                  })()}
                  {students.length === 0 && (
                    <tr><td colSpan={8} className="text-muted">Aucun élève pour l'instant.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ------- ONGLET TÉLÉPHONES ------- */}
        {tab === "phones" && (
          <>
            <div className="mb-2" style={{ maxWidth: 420 }}>
              <input
                className="form-control"
                placeholder="Recherche: nom, email, téléphone"
                value={phoneQuery}
                onChange={(e) => setPhoneQuery(e.target.value)}
              />
            </div>
            <div className="table-responsive">
              <table className="table table-sm align-middle table-light transparent-card">
                <thead>
                  <tr>
                    <th>UID</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Consentement</th>
                    <th>Enregistré le</th>
                  </tr>
                </thead>
                <tbody>
                  {phones
                    .filter((p) => {
                      const q = phoneQuery.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (p.displayName || "-").toLowerCase().includes(q) ||
                        (p.email || "-").toLowerCase().includes(q) ||
                        (p.phone || "-").toLowerCase().includes(q)
                      );
                    })
                    .map((p) => (
                      <tr key={p.id}>
                        <td className="text-truncate" style={{ maxWidth: 220 }}><code>{p.id}</code></td>
                        <td>{p.displayName || "-"}</td>
                        <td>{p.email || "-"}</td>
                        <td>{p.phone || "-"}</td>
                        <td>
                          <span className={`badge ${p.consent ? "bg-success" : "bg-secondary"}`}>
                            {p.consent ? "Oui" : "Non"}
                          </span>
                        </td>
                        <td>{p.createdAtISO ? new Date(p.createdAtISO).toLocaleString("fr-FR") : "-"}</td>
                      </tr>
                    ))}
                  {phones.length === 0 && (
                    <tr><td colSpan={6} className="text-muted">Aucun téléphone enregistré.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ------- ONGLET CODES DE PARRAINAGE ------- */}
        {tab === "codes" && (
          <>
            <div className="card transparent-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Créer un code de parrainage</h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Code</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex: ABCD-EFGH-IJKL"
                      value={newCode}
                      onChange={(e) => setNewCode(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Réduction (DH)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newDiscount}
                      onChange={(e) => setNewDiscount(e.target.value)}
                      min="1"
                      max="100"
                    />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button 
                      className="btn btn-primary w-100"
                      onClick={createReferralCode}
                    >
                      Créer
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle table-light transparent-card">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Réduction</th>
                    <th>Statut</th>
                    <th>Utilisations</th>
                    <th>Créé le</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {refCodes.map((code) => (
                    <tr key={code.id}>
                      <td><code>{code.id}</code></td>
                      <td>{code.discount || 50} DH</td>
                      <td>
                        <span className={`badge ${code.active ? 'bg-success' : 'bg-danger'}`}>
                          {code.active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>{code.usedCount || 0}</td>
                      <td>
                        {code.createdAt?.toDate?.()?.toLocaleDateString('fr-FR') || '-'}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className={`btn btn-sm ${code.active ? 'btn-warning' : 'btn-success'}`}
                            onClick={() => toggleCode(code.id, code.active)}
                          >
                            {code.active ? 'Désactiver' : 'Activer'}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteCode(code.id)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {refCodes.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted text-center">
                        Aucun code de parrainage
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ------- ONGLET PAYEMENT PROFS ------- */}
        {tab === "payments" && (
          <>
            <div className="card transparent-card mb-3">
              <div className="card-body d-flex align-items-center gap-3 flex-wrap">
                <div className="btn-group" role="group" aria-label="Section">
                  <button className={"btn btn-sm " + (paySection === "2bac" ? "btn-primary" : "btn-outline-primary")} onClick={() => setPaySection("2bac")}>2Bac</button>
                  <button className={"btn btn-sm " + (paySection === "3acfr" ? "btn-primary" : "btn-outline-primary")} onClick={() => setPaySection("3acfr")}>3AC Fr</button>
                </div>
                {payErr && <div className="alert alert-warning mb-0 py-1 px-2">{payErr}</div>}
                {/* Petit debug temporaire */}
                <div style={{fontSize: "12px", color: "#666", textAlign: "center", marginLeft: "auto"}}>
                  [Debug] Profs: {payRows.length}
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle table-light transparent-card">
                <thead>
                  <tr>
                    <th>Prof UID</th>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Matière</th>
                    <th>Inscriptions avec parrainage ({paySection})</th>
                    <th>Bonus</th>
                    <th>Payés</th>
                    <th>Restants</th>
                    <th style={{ width: 220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payRows.map((r) => {
                    const key = `${r.profId}__${paySection}`;
                    const draft = paidEdits[key];
                    const paidVal = Number.isFinite(draft) ? draft : r.paidCount;
                    const rest = Math.max(0, (r.referralCount || 0) - (Number.isFinite(paidVal) ? paidVal : 0));
                    return (
                      <tr key={key}>
                        <td><code>{r.profId}</code></td>
                        <td>{r.displayName || "-"}</td>
                        <td>{r.email || "-"}</td>
                        <td>{r.subject || "-"}</td>
                        <td>{r.referralCount || 0}</td>
                        <td className="text-success">{r.bonusesLabel || ""}</td>
                        <td>
                          <input
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                            style={{ maxWidth: 100 }}
                            value={Number.isFinite(paidVal) ? paidVal : 0}
                            onChange={(e) => setPaidEdits((prev) => ({ ...prev, [key]: parseInt(e.target.value || "0", 10) }))}
                          />
                          {plusFlash === key && (
                            <span className="ms-2 flash-up">+1</span>
                          )}
                        </td>
                        <td>{rest}</td>
                        <td>
                          <div className="btn-group me-2" role="group" aria-label="Incrémenter">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                const next = Math.max(0, (Number.isFinite(paidVal) ? paidVal : 0) - 1);
                                setPaidEdits((prev) => ({ ...prev, [key]: next }));
                              }}
                              title="−1"
                            >−</button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success pop-anim"
                              onClick={() => {
                                const cap = Math.max(0, r.referralCount || 0);
                                const next = Math.min(cap, (Number.isFinite(paidVal) ? paidVal : 0) + 1);
                                setPaidEdits((prev) => ({ ...prev, [key]: next }));
                                setPlusFlash(key);
                                setTimeout(() => setPlusFlash(null), 700);
                              }}
                              title="+1"
                            >+</button>
                          </div>
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={payBusy}
                            onClick={async () => {
                              const count = Number.isFinite(paidVal) ? paidVal : 0;
                              setPayConfirm({ key, row: r, count });
                            }}
                          >
                            Enregistrer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {payRows.length === 0 && (
                    <tr><td colSpan={9} className="text-muted">Aucune donnée de parrainage pour cette section.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

            {/* Animations CSS */}
            <style>{`
              @keyframes popScale { 0%{ transform: scale(1);} 50%{ transform: scale(1.15);} 100%{ transform: scale(1);} }
              .pop-anim { animation: popScale 300ms ease-in-out; }
              @keyframes flashUp { 0%{opacity:0; transform: translateY(6px);} 20%{opacity:1; transform: translateY(0);} 100%{opacity:0; transform: translateY(-6px);} }
              .flash-up { animation: flashUp 700ms ease-out; color: #198754; font-weight: 600; }
            `}</style>

            {/* Confirmation PAIEMENT */}
            {payConfirm && (
              <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 1050 }}>
                <div className="w-100 h-100" style={{ background: "rgba(0,0,0,.35)" }} onClick={() => setPayConfirm(null)} />
                <div className="position-absolute top-50 start-50 translate-middle" style={{ minWidth: 480, maxWidth: 640 }}>
                  <div className="card shadow-lg">
                    <div className="card-body">
                      <h5 className="card-title mb-3">Valider le paiement</h5>
                      <div className="mb-2 small text-muted">{paySection === "3acfr" ? "3AC Fr" : "2Bac"} · {payConfirm.row.displayName || payConfirm.row.email || payConfirm.row.profId}</div>
                      <div className="mb-3">
                        <div className="mb-2">Nombre à régler: <span className="badge text-bg-primary">{payConfirm.count}</span></div>
                        <div className="mb-2">Avec parrainage total: <span className="badge text-bg-secondary">{payConfirm.row.referralCount || 0}</span></div>
                        <div className="mb-2">Restants après validation: <span className="badge text-bg-warning">{Math.max(0, (payConfirm.row.referralCount || 0) - (payConfirm.count || 0))}</span></div>
                        <div className="border rounded p-2" style={{ maxHeight: 220, overflow: "auto" }}>
                          {Array.from({ length: Math.max(0, payConfirm.count || 0) }, (_, i) => i + 1).map((n) => (
                            <div key={n} className="d-flex align-items-center gap-2 py-1">
                              <span className="badge text-bg-light">#{n}</span>
                              <span className="small text-muted">Paiement {n}</span>
                            </div>
                          ))}
                          {(!payConfirm.count || payConfirm.count <= 0) && (
                            <div className="small text-muted">Aucun élément</div>
                          )}
                        </div>
                        {(payConfirm.count || 0) > (payConfirm.row.referralCount || 0) && (
                          <div className="alert alert-warning mt-2 py-1 px-2">Le nombre dépasse les inscriptions avec parrainage.</div>
                        )}
                      </div>
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-outline-secondary" onClick={() => setPayConfirm(null)}>Annuler</button>
                        <button className="btn btn-primary" disabled={payBusy} onClick={async () => {
                          try {
                            setPayBusy(true);
                            await setDoc(
                              doc(db, "profPayments", payConfirm.key),
                              {
                                profId: payConfirm.row.profId,
                                section: paySection,
                                paidCount: Math.max(0, payConfirm.count || 0),
                                updatedAt: serverTimestamp(),
                              },
                              { merge: true }
                            );
                            setPayConfirm(null);
                          } catch (e) {
                            setPayErr(e.message || "Erreur lors de l'enregistrement");
                          } finally {
                            setPayBusy(false);
                          }
                        }}>Valider</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Viewer />

            <style>{`
              .admin-bg {
                position: fixed;
                inset: 0;
                z-index: -1;
                background-image: var(--admin-bg);
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                background-attachment: fixed;
                transform: translateZ(0);
              }
              /* voile léger pour la lisibilité du contenu */
              .admin-bg::after {
                content: "";
                position: absolute;
                inset: 0;
                background: linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.3));
              }
              /* sur mobile/tablette on évite le fixed (iOS/Android) */
              @media (max-width: 991.98px) {
                .admin-bg { background-attachment: scroll; }
              }

              /* Cartes transparentes pour Admin.jsx */
              .transparent-card {
                background: rgba(255, 255, 255, 0.85) !important;
                backdrop-filter: blur(10px);
              }

              .sv-backdrop {
                position: fixed; 
                inset: 0; 
                background: rgba(0,0,0,.6);
                display: flex; 
                align-items: center; 
                justify-content: center; 
                z-index: 1050;
              }
              .sv-inner {
                position: relative; 
                width: min(1200px,95vw); 
                height: min(80vh,95vh);
                background: #000; 
                border-radius: 12px; 
                overflow: hidden;
                box-shadow: 0 10px 30px rgba(0,0,0,.4);
              }
              .sv-close { 
                position: absolute; 
                top: 8px; 
                right: 8px; 
              }
            `}</style>
          </div>
        </>
      );
    }



